/**
 * Item Service
 * Functions to interact with item Cloud Functions
 */

import { httpsCallable } from 'firebase/functions'
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore'
import { functions, db } from '@/config/firebase'
import { WishlistItem, ItemFormData } from '@/types'

/**
 * Create a new item
 */
export async function createItem(wishlistId: string, data: ItemFormData) {
  const createItemFn = httpsCallable(functions, 'createItem')
  const result = await createItemFn({ wishlistId, ...data })
  return result.data as { id: string; message: string }
}

/**
 * Update an item
 */
export async function updateItem(
  wishlistId: string,
  itemId: string,
  data: Partial<ItemFormData>
) {
  const updateItemFn = httpsCallable(functions, 'updateItem')
  const result = await updateItemFn({ wishlistId, itemId, ...data })
  return result.data as { message: string }
}

/**
 * Delete an item
 */
export async function deleteItem(wishlistId: string, itemId: string) {
  const deleteItemFn = httpsCallable(functions, 'deleteItem')
  const result = await deleteItemFn({ wishlistId, itemId })
  return result.data as { message: string }
}

/**
 * Reorder items
 */
export async function reorderItems(
  wishlistId: string,
  items: Array<{ id: string; order: number }>
) {
  const reorderItemsFn = httpsCallable(functions, 'reorderItems')
  const result = await reorderItemsFn({ wishlistId, items })
  return result.data as { message: string }
}

/**
 * Get all items for a wishlist
 */
export async function getWishlistItems(wishlistId: string): Promise<WishlistItem[]> {
  const q = query(
    collection(db, 'wishlists', wishlistId, 'items'),
    orderBy('order', 'asc')
  )

  const snapshot = await getDocs(q)
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as WishlistItem[]
}

/**
 * Get items by category
 */
export async function getItemsByCategory(
  wishlistId: string,
  categoryId: string | null
): Promise<WishlistItem[]> {
  const q = query(
    collection(db, 'wishlists', wishlistId, 'items'),
    where('categoryId', '==', categoryId),
    orderBy('order', 'asc')
  )

  const snapshot = await getDocs(q)
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as WishlistItem[]
}

/**
 * Subscribe to wishlist items
 */
export function subscribeToWishlistItems(
  wishlistId: string,
  callback: (items: WishlistItem[]) => void
): Unsubscribe {
  const q = query(
    collection(db, 'wishlists', wishlistId, 'items'),
    orderBy('order', 'asc')
  )

  return onSnapshot(q, snapshot => {
    const items = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as WishlistItem[]
    callback(items)
  })
}
